'use client';

import { useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  cleanFileNames,
  cleanSmartQuotes,
  cleanUnicodeText,
  convertKeyboardLayout,
  convertTextCase,
  createTextFixJson,
  fixWrongKeyboardLayout,
  generateSlug,
  normalizeLineBreaks,
  removeExtraSpaces,
  translitToAscii,
  type CaseMode,
  type KeyboardLayoutPreset,
  type LineBreakMode,
  type SeparatorMode,
} from '@/lib/browser-text-fix-tools';

type Variant =
  | 'keyboard-layout-converter'
  | 'fix-wrong-keyboard-layout'
  | 'translit-converter'
  | 'case-converter'
  | 'slug-generator'
  | 'filename-cleaner'
  | 'remove-extra-spaces'
  | 'line-break-normalizer'
  | 'unicode-cleaner'
  | 'smart-quotes-cleaner';

type ToolResult =
  | {
      kind: 'text';
      output: string;
      note?: string | null;
    }
  | {
      kind: 'keyboard-fix';
      bestLabel: string;
      bestOutput: string;
      alternatives: Array<{ label: string; output: string }>;
      jsonText: string;
    };

const sampleWrongLayout = 'ghbdtn, rfr ltkf?';
const sampleText = `“Cleanor” — browser-first tools…

Привіт  world   with   extra   spaces`;
const sampleFilename = `Мій файл 2026?.pdf
Résumé final .docx
report<>draft.csv`;

const keyboardPresetOptions: Array<{ value: KeyboardLayoutPreset; label: string }> = [
  { value: 'en-ru', label: 'EN -> RU' },
  { value: 'ru-en', label: 'RU -> EN' },
  { value: 'en-ua', label: 'EN -> UA' },
  { value: 'ua-en', label: 'UA -> EN' },
];

const caseModeOptions: Array<{ value: CaseMode; label: string }> = [
  { value: 'lowercase', label: 'lowercase' },
  { value: 'uppercase', label: 'UPPERCASE' },
  { value: 'title-case', label: 'Title Case' },
  { value: 'sentence-case', label: 'Sentence case' },
  { value: 'swap-case', label: 'Swap Case' },
];

const separatorOptions: Array<{ value: SeparatorMode; label: string }> = [
  { value: 'dash', label: 'Dash (-)' },
  { value: 'underscore', label: 'Underscore (_)' },
];

const lineBreakOptions: Array<{ value: LineBreakMode; label: string }> = [
  { value: 'lf', label: 'LF' },
  { value: 'crlf', label: 'CRLF' },
];

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    placeholder: string;
    downloadName: string;
  }
> = {
  'keyboard-layout-converter': {
    title: 'Convert text between supported keyboard layouts',
    description:
      'Paste text, choose the source and target keyboard layout preset, and convert the keystrokes locally without translation or spelling changes.',
    badge: 'Layout',
    actionLabel: 'Convert layout',
    successLabel: 'Keyboard layout conversion',
    placeholder: sampleWrongLayout,
    downloadName: 'keyboard-layout.txt',
  },
  'fix-wrong-keyboard-layout': {
    title: 'Fix text typed in the wrong keyboard layout',
    description:
      'Paste mistyped text, detect the most likely keyboard-layout mistake, and review the best correction plus alternate candidates locally.',
    badge: 'Fix',
    actionLabel: 'Fix layout',
    successLabel: 'Wrong keyboard layout fix',
    placeholder: sampleWrongLayout,
    downloadName: 'fixed-layout.txt',
  },
  'translit-converter': {
    title: 'Convert practical multilingual text into Latin ASCII',
    description:
      'Paste text, transliterate Cyrillic and normalize accented Latin input into simpler ASCII output for cleaner reuse.',
    badge: 'Translit',
    actionLabel: 'Transliterate',
    successLabel: 'Transliteration',
    placeholder: sampleText,
    downloadName: 'translit.txt',
  },
  'case-converter': {
    title: 'Convert text casing without opening a heavier editor',
    description:
      'Paste content, choose the target case mode, and export cleaner text for docs, support replies, or content cleanup.',
    badge: 'Case',
    actionLabel: 'Convert case',
    successLabel: 'Case conversion',
    placeholder: sampleText,
    downloadName: 'case-converted.txt',
  },
  'slug-generator': {
    title: 'Generate a clean ASCII slug from pasted text',
    description:
      'Paste a title or phrase, transliterate it, collapse separators, and export a cleaner slug for URLs, anchors, or content handoff.',
    badge: 'Slug',
    actionLabel: 'Generate slug',
    successLabel: 'Slug generation',
    placeholder: 'Привіт, Cleanor launch page 2026',
    downloadName: 'slug.txt',
  },
  'filename-cleaner': {
    title: 'Clean pasted filenames without touching uploaded files',
    description:
      'Paste one filename or a newline-separated list, preserve extensions, and clean the names into safer ASCII-first output locally.',
    badge: 'File',
    actionLabel: 'Clean filenames',
    successLabel: 'Filename cleanup',
    placeholder: sampleFilename,
    downloadName: 'filenames.txt',
  },
  'remove-extra-spaces': {
    title: 'Remove repeated spaces and tabs from pasted text',
    description:
      'Collapse repeated spaces and tabs, trim each line, and keep paragraph structure in one cleaner text block.',
    badge: 'Space',
    actionLabel: 'Remove spaces',
    successLabel: 'Whitespace cleanup',
    placeholder: sampleText,
    downloadName: 'clean-spaces.txt',
  },
  'line-break-normalizer': {
    title: 'Normalize mixed line endings into one output mode',
    description:
      'Paste text with mixed line endings, choose LF or CRLF output, and export normalized text without rewriting the content itself.',
    badge: 'Lines',
    actionLabel: 'Normalize line breaks',
    successLabel: 'Line break normalization',
    placeholder: sampleText,
    downloadName: 'normalized-lines.txt',
  },
  'unicode-cleaner': {
    title: 'Remove invisible Unicode trouble characters',
    description:
      'Normalize troublesome Unicode characters like BOM, zero-width marks, and non-breaking spaces while keeping visible text intact.',
    badge: 'Unicode',
    actionLabel: 'Clean Unicode',
    successLabel: 'Unicode cleanup',
    placeholder: `\uFEFFCleanor\u00A0launch\u200Bnotes`,
    downloadName: 'unicode-cleaned.txt',
  },
  'smart-quotes-cleaner': {
    title: 'Convert smart quotes and typography punctuation to plain ASCII',
    description:
      'Replace curly quotes, normalize dashes, and turn ellipses into plain-text equivalents for cleaner copy and support workflows.',
    badge: 'Quotes',
    actionLabel: 'Clean smart quotes',
    successLabel: 'Typography cleanup',
    placeholder: sampleText,
    downloadName: 'plain-punctuation.txt',
  },
};

function createTextDownload(text: string, fileName: string, mimeType = 'text/plain;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

export default function BrowserTextFixTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [inputValue, setInputValue] = useState('');
  const [keyboardPreset, setKeyboardPreset] = useState<KeyboardLayoutPreset>('en-ru');
  const [caseMode, setCaseMode] = useState<CaseMode>('lowercase');
  const [separatorMode, setSeparatorMode] = useState<SeparatorMode>('dash');
  const [lineBreakMode, setLineBreakMode] = useState<LineBreakMode>('lf');
  const [result, setResult] = useState<ToolResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  function resetOutput() {
    setResult(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  async function runTool() {
    if (!inputValue.trim()) {
      setErrorMessage('Paste some text first.');
      return;
    }

    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another text-fix run right now.',
      );

      switch (variant) {
        case 'keyboard-layout-converter': {
          const output = convertKeyboardLayout(inputValue, keyboardPreset);
          const presetLabel =
            keyboardPresetOptions.find((entry) => entry.value === keyboardPreset)?.label ??
            keyboardPreset;
          setResult({ kind: 'text', output, note: `Preset: ${presetLabel}` });
          break;
        }
        case 'fix-wrong-keyboard-layout': {
          const fixed = fixWrongKeyboardLayout(inputValue);
          setResult({
            kind: 'keyboard-fix',
            bestLabel: fixed.best.label,
            bestOutput: fixed.best.output,
            alternatives: fixed.alternatives.map((entry) => ({
              label: entry.label,
              output: entry.output,
            })),
            jsonText: createTextFixJson(fixed.all),
          });
          break;
        }
        case 'translit-converter': {
          setResult({
            kind: 'text',
            output: translitToAscii(inputValue),
            note: 'ASCII transliteration',
          });
          break;
        }
        case 'case-converter': {
          setResult({
            kind: 'text',
            output: convertTextCase(inputValue, caseMode),
            note: `Mode: ${caseModeOptions.find((entry) => entry.value === caseMode)?.label ?? caseMode}`,
          });
          break;
        }
        case 'slug-generator': {
          setResult({
            kind: 'text',
            output: generateSlug(inputValue, separatorMode),
            note: `Separator: ${separatorMode === 'dash' ? '-' : '_'}`,
          });
          break;
        }
        case 'filename-cleaner': {
          setResult({
            kind: 'text',
            output: cleanFileNames(inputValue),
            note: 'ASCII-first safe filenames',
          });
          break;
        }
        case 'remove-extra-spaces': {
          setResult({
            kind: 'text',
            output: removeExtraSpaces(inputValue),
            note: 'Repeated spaces and tabs collapsed',
          });
          break;
        }
        case 'line-break-normalizer': {
          setResult({
            kind: 'text',
            output: normalizeLineBreaks(inputValue, lineBreakMode),
            note: `Output mode: ${lineBreakMode.toUpperCase()}`,
          });
          break;
        }
        case 'unicode-cleaner': {
          setResult({
            kind: 'text',
            output: cleanUnicodeText(inputValue),
            note: 'Invisible Unicode cleaned and NFKC normalized',
          });
          break;
        }
        case 'smart-quotes-cleaner': {
          setResult({
            kind: 'text',
            output: cleanSmartQuotes(inputValue),
            note: 'Curly quotes and typography punctuation normalized',
          });
          break;
        }
      }

      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'This text-fix task could not be completed.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy(text: string, label: string) {
    try {
      await copyText(text);
      setCopyMessage(label);
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current output.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'keyboard-layout-converter' ? (
              <label className="tool-live-field">
                <span>Layout preset</span>
                <select
                  onChange={(event) => {
                    resetOutput();
                    setKeyboardPreset(event.target.value as KeyboardLayoutPreset);
                  }}
                  value={keyboardPreset}
                >
                  {keyboardPresetOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {variant === 'case-converter' ? (
              <label className="tool-live-field">
                <span>Case mode</span>
                <select
                  onChange={(event) => {
                    resetOutput();
                    setCaseMode(event.target.value as CaseMode);
                  }}
                  value={caseMode}
                >
                  {caseModeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {variant === 'slug-generator' ? (
              <label className="tool-live-field">
                <span>Separator</span>
                <select
                  onChange={(event) => {
                    resetOutput();
                    setSeparatorMode(event.target.value as SeparatorMode);
                  }}
                  value={separatorMode}
                >
                  {separatorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {variant === 'line-break-normalizer' ? (
              <label className="tool-live-field">
                <span>Output line ending</span>
                <select
                  onChange={(event) => {
                    resetOutput();
                    setLineBreakMode(event.target.value as LineBreakMode);
                  }}
                  value={lineBreakMode}
                >
                  {lineBreakOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <label className="tool-live-field tool-live-field-full">
              <span>{variant === 'filename-cleaner' ? 'Filename input' : 'Text input'}</span>
              <textarea
                onChange={(event) => {
                  resetOutput();
                  setInputValue(event.target.value);
                }}
                placeholder={copy.placeholder}
                spellCheck={variant !== 'slug-generator' && variant !== 'filename-cleaner'}
                value={inputValue}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void runTool()}
              type="button"
            >
              {isRunning ? 'Working…' : copy.actionLabel}
            </button>
          </div>

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {result?.kind === 'text' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.output, 'Output copied.')}
                  type="button"
                >
                  Copy output
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => createTextDownload(result.output, copy.downloadName)}
                  type="button"
                >
                  Download text
                </button>
              </div>
              {result.note ? (
                <div className="tool-live-panel-note">
                  <strong>Summary</strong>
                  <span>{result.note}</span>
                </div>
              ) : null}
              <div className="tool-live-preview">
                <h3>Output</h3>
                <pre className="tool-markdown-code">{result.output}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'keyboard-fix' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.bestOutput, 'Best correction copied.')}
                  type="button"
                >
                  Copy best fix
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => createTextDownload(result.bestOutput, copy.downloadName)}
                  type="button"
                >
                  Download text
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.jsonText, 'Candidate JSON copied.')}
                  type="button"
                >
                  Copy JSON
                </button>
              </div>
              <div className="tool-live-panel-note">
                <strong>Detected layout pair</strong>
                <span>{result.bestLabel}</span>
              </div>
              <div className="tool-live-preview">
                <h3>Best correction</h3>
                <pre className="tool-markdown-code">{result.bestOutput}</pre>
              </div>
              <div className="tool-html-issue-list">
                {result.alternatives.map((candidate) => (
                  <article className="tool-html-issue" key={candidate.label}>
                    <strong>{candidate.label}</strong>
                    <p>{candidate.output || 'No output'}</p>
                  </article>
                ))}
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
